import { Link } from 'react-router-dom';
import type { VideoCard as VideoCardPayload } from '@nk/shared';
import { useLocale } from '@/i18n/locale-context';
import { formatDuration } from '@/lib/format';
import { VideoCard } from '@/components/video';
import { ArticleImage } from '@/components/content';
import { Icon } from '@/components/shell';

/**
 * Video coverage on the homepage — PRD §13, promoted.
 *
 * This used to be a four-card strip at the foot of the page, which made a newsroom that
 * shoots as much as it writes look like one that only writes. It is now a section of its
 * own directly under the hero: one lead video at the size a video deserves, with the rest
 * of the desk's recent coverage beside it.
 *
 * Written and video coverage stay in separate sections rather than being interleaved into
 * one feed. A reader who came for one can skip to the other, and the mixed feed the
 * alternative produces hides whichever kind is currently rarer.
 *
 * The cards themselves come from `components/video` (Phase 12), so a video card is the
 * same object here, on `/videos`, and in an article's sidebar. Only the lead is drawn
 * here, because only the lead is unique to this page.
 */
export function VideoCoverage({ videos }: { videos: VideoCardPayload[] }) {
  const [lead, ...rest] = videos;
  if (!lead) return null;

  return (
    <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
      <LeadVideo video={lead} />
      <div className="grid grid-cols-2 gap-4 self-start sm:grid-cols-4 lg:grid-cols-2">
        {/* Four beside the lead on a wide screen, and the aggregate sends more than that
            so an unpublished video still leaves a full grid. */}
        {rest.slice(0, 4).map((video) => (
          <VideoCard key={video.id} video={video} />
        ))}
      </div>
    </div>
  );
}

/**
 * The lead is a link to the video page, not an embedded player: PRD §13 puts playback on
 * the video page, and an autoplaying iframe at the top of a homepage is both a
 * performance cost on every visit and the thing readers on metered connections in Assam
 * complain about first. What it does carry is a play affordance big enough to read as
 * one.
 */
function LeadVideo({ video }: { video: VideoCardPayload }) {
  const { locale, t } = useLocale();

  return (
    <Link to={video.path} data-video-lead className="group block overflow-hidden rounded-lg">
      <span className="relative block aspect-video overflow-hidden rounded-lg bg-soft">
        {video.thumbnail ? (
          <ArticleImage
            image={video.thumbnail}
            alt={video.title}
            aspect="aspect-video"
            sizes="(max-width: 1080px) 100vw, 55vw"
            className="h-full w-full transition duration-500 group-hover:scale-[1.03]"
          />
        ) : video.thumbnailUrl ? (
          <img
            src={video.thumbnailUrl}
            alt=""
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
          />
        ) : (
          <span className="block h-full w-full bg-soft" aria-hidden="true" />
        )}

        <span
          aria-hidden="true"
          className="absolute inset-0 bg-[linear-gradient(0deg,rgba(17,20,24,.85),transparent_55%)]"
        />

        {video.durationSeconds ? (
          <span className="absolute left-3 top-3 z-10 rounded-sm bg-black/70 px-2 py-0.5 text-xs font-bold text-white">
            {formatDuration(video.durationSeconds, locale)}
          </span>
        ) : null}

        <span
          aria-hidden="true"
          className="absolute left-1/2 top-1/2 z-10 grid h-16 w-16 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-white/90 transition group-hover:scale-110"
        >
          <Icon name="play" size={24} className="ml-1 text-red" />
        </span>

        <span className="absolute inset-x-0 bottom-0 z-10 p-4 lg:p-5">
          <span className="block text-lg font-extrabold text-white transition group-hover:underline lg:text-2xl">
            {video.title}
          </span>
          {video.category && (
            <span className="mt-1.5 block text-xs font-bold uppercase tracking-wide text-[#cfd2d6]">
              {video.category.name}
            </span>
          )}
        </span>
        <span className="sr-only">{t('action.play')}</span>
      </span>
    </Link>
  );
}
